/**
 * «Обзвон»: сделки, которым сейчас нужен звонок, и у кого они. Спека
 * `docs/specs/2026-09-24-deal-call-list-design.md`; числа — те же, что у
 * `prokat-runner deals-call`, из одного модуля на сервере. Вкладка «Дни»
 * (`?tab=days`) — `DialingDays.tsx`, `docs/specs/2026-09-26-dialing-days-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { Link, useSearchParams } from "react-router";

import { bitrixUrl } from "@/lib/calls";
import { type CallList, type DealRow, loadCallList } from "@/lib/dialing";
import { shortTime } from "@/lib/leads";
import { DaysTab } from "@/pages/DialingDays";

/** Фильтр списка — в адресе (`?user=&type=&attempt=&now=1`): ссылку можно переслать. */
type Filter = { user: number | null; type: number | null; attempt: number | null; now: boolean };

function useFilter() {
  const [params, setParams] = useSearchParams();
  const num = (k: string) => (params.get(k) === null ? null : Number(params.get(k)));
  const f: Filter = { user: num("user"), type: num("type"), attempt: num("attempt"), now: params.get("now") === "1" };
  const set = (next: Filter) => {
    const q: Record<string, string> = {};
    if (next.user !== null) q.user = String(next.user);
    if (next.type !== null) q.type = String(next.type);
    if (next.attempt !== null) q.attempt = String(next.attempt);
    if (next.now) q.now = "1";
    setParams(q, { replace: true });
  };
  return [f, set] as const;
}

const keep = (f: Filter) => (d: DealRow) =>
  (f.user === null || d.user_id === f.user) &&
  (f.type === null || d.type_id === f.type) &&
  (f.attempt === null || d.attempt_group === f.attempt) &&
  (!f.now || d.callable_now);

const money = (v: number | null) => (v === null ? "—" : Math.round(v).toLocaleString("ru-RU"));
const dm = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : "—");

function Tiles({ r, f, set }: { r: CallList; f: Filter; set: (f: Filter) => void }) {
  const t = r.totals;
  const none: Filter = { user: null, type: null, attempt: null, now: false };
  const tiles: { label: string; value: number; hint: string; to: Filter; active: boolean; tone?: string }[] = [
    { label: "В обзвоне", value: t.deals, hint: "правила CRM «Обзвон: нужен», салон есть", to: none, active: f.user === null && !f.now && f.type === null },
    { label: "Можно звонить сейчас", value: t.callable_now, hint: `по времени салона ${r.work_hours}`, to: { ...none, now: true }, active: f.now },
    { label: "Не назначено", value: t.unassigned, hint: "ответственного нет", to: { ...none, user: 0 }, active: f.user === 0, tone: t.unassigned ? "text-warn" : "" },
    { label: "Назначено", value: t.assigned, hint: "есть ответственный", to: none, active: false },
  ];
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-2" data-annot="итоги обзвона">
      <div className="grid grid-cols-2 gap-1 lg:grid-cols-4">
        {tiles.map((x) => (
          <button
            key={x.label}
            type="button"
            onClick={() => set(x.to)}
            className={`flex min-w-0 flex-col gap-1 rounded-[var(--radius-ui)] border p-3 text-left hover:bg-bg-hover ${x.active ? "border-fg" : "border-transparent"}`}
          >
            <span className="text-[12px] text-fg-muted">{x.label}</span>
            <span className={`text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums ${x.tone ?? ""}`}>{x.value}</span>
            <span className="text-[11.5px] text-fg-faint">{x.hint}</span>
          </button>
        ))}
      </div>
      <div className="px-3 pb-1 pt-2 text-[11.5px] text-fg-faint">
        Сделки без салона (города без кассы) не показаны: {t.no_salon} — как фильтр CRM «Салон: есть».
      </div>
    </section>
  );
}

/** Сводка «ответственный × тип»: итог крупно, ниже — по числу звонков. Клик фильтрует список. */
function Summary({ r, f, set }: { r: CallList; f: Filter; set: (f: Filter) => void }) {
  const types = r.types.filter((t) => r.users.some((u) => u.cells[t.id]));
  const pick = (user: number | null, type: number | null, attempt: number | null = null) =>
    set({ user, type, attempt, now: f.now });
  const on = (user: number | null, type: number | null, attempt: number | null = null) =>
    f.user === user && f.type === type && f.attempt === attempt;
  const btn = (active: boolean) => `rounded-[var(--radius-ui)] px-1.5 hover:bg-bg-hover ${active ? "ring-1 ring-fg" : ""}`;
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot="сводка обзвона">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-3 py-1.5 font-medium">Ответственный</th>
              <th className="px-3 py-1.5 font-medium">Итого</th>
              {types.map((t) => (
                <th key={t.id} className="whitespace-nowrap px-3 py-1.5 font-medium">
                  <button type="button" onClick={() => pick(null, t.id)} className={`${btn(on(null, t.id))} text-fg-muted`}>
                    {t.name}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.users.map((u) => (
              <tr key={u.user_id} className="border-t border-border" data-annot={`ответственный ${u.name}`}>
                <td className="whitespace-nowrap px-3 py-2">
                  <button type="button" onClick={() => pick(u.user_id, null)} className={`${btn(on(u.user_id, null))} ${u.user_id ? "" : "text-warn"}`}>
                    {u.name}
                  </button>
                </td>
                <td className="px-3 py-2 text-[15px] font-semibold tabular-nums">{u.total}</td>
                {types.map((t) => {
                  const c = u.cells[t.id];
                  if (!c) return <td key={t.id} className="px-3 py-2 text-fg-faint">—</td>;
                  return (
                    <td key={t.id} className="whitespace-nowrap px-2 py-1">
                      <button type="button" onClick={() => pick(u.user_id, t.id)} className={`${btn(on(u.user_id, t.id))} text-[15px] font-semibold tabular-nums`}>
                        {c.reduce((a, b) => a + b, 0)}
                      </button>
                      <div className="flex gap-0.5 text-[11px] tabular-nums text-fg-muted">
                        {c.map((n, i) => (
                          <button
                            key={i}
                            type="button"
                            disabled={!n}
                            onClick={() => pick(u.user_id, t.id, i)}
                            title={`звонков ${r.attempt_groups[i]}`}
                            className={`${btn(on(u.user_id, t.id, i))} disabled:text-fg-faint disabled:hover:bg-transparent`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11.5px] text-fg-faint">
        Мелкие числа — сделки по количеству звонков за всё время: {r.attempt_groups.join(" · ")}. Клик по числу, имени
        или типу — эти сделки в списке ниже.
      </div>
    </section>
  );
}

const COLUMNS = ["Сделка", "Тип", "Стадия", "Салон", "След. обработка", "Прокат", "Сумма / оплачено", "Звонков", "Посл. звонок", "Посл. разговор", "Ответственный"];

function Deals({ r, f, set, anchor }: { r: CallList; f: Filter; set: (f: Filter) => void; anchor: React.RefObject<HTMLElement | null> }) {
  const rows = r.deals.filter(keep(f));
  const typeName = Object.fromEntries(r.types.map((t) => [t.id, t.name]));
  const userName = (id: number) => r.users.find((u) => u.user_id === id)?.name ?? `#${id}`;
  const chips = [
    f.user !== null && userName(f.user),
    f.type !== null && typeName[f.type],
    f.attempt !== null && `звонков ${r.attempt_groups[f.attempt]}`,
    f.now && "можно звонить сейчас",
  ].filter(Boolean) as string[];
  return (
    <section ref={anchor} className="scroll-mt-4 rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="список сделок обзвона">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="mr-2 text-[13px] font-medium">Сделки: {rows.length}</h2>
        {chips.map((c) => (
          <span key={c} className="inline-flex h-7 items-center rounded-[var(--radius-ui)] border border-fg bg-bg px-2.5 text-[12px] text-fg">
            {c}
          </span>
        ))}
        {chips.length > 0 && (
          <button
            type="button"
            onClick={() => set({ user: null, type: null, attempt: null, now: false })}
            className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] text-fg-muted hover:bg-bg-hover"
          >
            сбросить
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Нет сделок.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c} className="whitespace-nowrap px-4 py-2 font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-border hover:bg-bg-hover" data-annot={`сделка ${d.id}`}>
                  <td className="whitespace-nowrap px-4 py-1.5">
                    <a
                      href={bitrixUrl("deal", d.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="tabular-nums text-fg underline decoration-border underline-offset-2 hover:decoration-fg"
                    >
                      {d.id}
                    </a>
                    <Link to={`/deals/${d.id}`} className="ml-1.5 text-[11.5px] text-accent hover:underline" title="карточка сделки: вся хронология">
                      лента
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-4 py-1.5">{typeName[d.type_id]}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 text-fg-muted">{d.stage ?? d.stage_id}</td>
                  <td className="whitespace-nowrap px-4 py-1.5">
                    {d.location ?? "—"}
                    <span className={`ml-2 text-[11.5px] tabular-nums ${d.callable_now ? "text-ok" : "text-fg-faint"}`} title="Местное время салона">
                      {d.local_time}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{shortTime(d.wait_at)}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
                    {d.rent_start || d.rent_end ? `${dm(d.rent_start)} – ${dm(d.rent_end)}` : "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
                    {money(d.sum)} <span className={d.paid ? "text-fg-muted" : "text-warn"}>/ {money(d.paid)}</span>
                  </td>
                  <td className="px-4 py-1.5 tabular-nums">{d.calls}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{shortTime(d.last_call_at)}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{shortTime(d.last_success_at)}</td>
                  <td className="whitespace-nowrap px-4 py-1.5">{d.user ?? <span className="text-warn">не назначен</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const TABS = [
  { id: "list", label: "Обзвон" },
  { id: "days", label: "Дни" },
] as const;

function Tabs() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "days" ? "days" : "list";
  return (
    <div role="tablist" aria-label="Разделы обзвона" className="flex gap-1 border-b border-border">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          onClick={() => setParams(t.id === "list" ? {} : { tab: t.id }, { replace: true })}
          className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-[13px] ${
            tab === t.id ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"
          }`}
          data-annot={`вкладка «${t.label}»`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function DialingPage() {
  const [params] = useSearchParams();
  if (params.get("tab") === "days") {
    return (
      <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
        <Tabs />
        <DaysTab />
      </main>
    );
  }
  return <ListTab />;
}

function ListTab() {
  const [f, set] = useFilter();
  const data = useQuery({ queryKey: ["deals-call-list"], queryFn: loadCallList, refetchInterval: 120_000 });
  const list = useRef<HTMLElement>(null);
  const pick = (next: Filter) => {
    set(next);
    list.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <Tabs />
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка обзвона">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Обзвон сделок</h1>
        {data.data && <span className="text-[12px] text-fg-faint">на {shortTime(data.data.now)}</span>}
        <button
          type="button"
          onClick={() => data.refetch()}
          disabled={data.isFetching}
          className="ml-auto h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[12.5px] text-fg-muted hover:bg-bg-hover disabled:opacity-50"
        >
          {data.isFetching ? "Обновляю…" : "Обновить"}
        </button>
      </div>
      {data.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по CRM…</div>
      ) : data.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить обзвон: {String(data.error.message)}</div>
      ) : (
        <>
          <Tiles r={data.data} f={f} set={pick} />
          <Summary r={data.data} f={f} set={pick} />
          <Deals r={data.data} f={f} set={set} anchor={list} />
        </>
      )}
    </main>
  );
}
